import React from "react";
import { inputStyle } from "../../Util/formInputStyle.js";

/** Blank answer state for a template: { [itemid]: value } */
export const blankAnswers = (template) =>
    Object.fromEntries((template?.items || []).map((it) => [it.itemid, it.kind === "CHECK" ? false : ""]));

/** Client-side mirror of sopModel.recordRun — per item: { ok, missing } */
export const evaluateItem = (item, value) => {
    if (item.kind === "CHECK") return { ok: value === true, missing: false };
    if (item.kind === "NUMBER") {
        if (value === "" || value == null) return { ok: !item.required, missing: !!item.required };
        const n = Number(value);
        if (Number.isNaN(n)) return { ok: false, missing: true };
        const lo = item.min_val != null && item.min_val !== "" ? Number(item.min_val) : null;
        const hi = item.max_val != null && item.max_val !== "" ? Number(item.max_val) : null;
        return { ok: (lo == null || n >= lo) && (hi == null || n <= hi), missing: false };
    }
    const filled = String(value || "").trim() !== "";
    return { ok: filled || !item.required, missing: item.required && !filled };
};

export const checklistState = (template, answers) => {
    const items = template?.items || [];
    const evals = items.map((it) => ({ it, ...evaluateItem(it, answers[it.itemid]) }));
    return {
        complete: evals.every((e) => !e.missing),
        pass: evals.every((e) => e.ok || !e.it.required),
        failed: evals.filter((e) => !e.ok && e.it.required).map((e) => e.it),
    };
};

export const toRunPayload = (template, answers, extra = {}) => ({
    templateid: template.templateid,
    answers: (template.items || []).map((it) => ({ itemid: it.itemid, value: answers[it.itemid] })),
    ...extra,
});

const rangeText = (it) => {
    const lo = it.min_val != null && it.min_val !== "" ? it.min_val : null;
    const hi = it.max_val != null && it.max_val !== "" ? it.max_val : null;
    if (lo == null && hi == null) return null;
    const u = it.unit ? ` ${it.unit}` : "";
    if (lo != null && hi != null) return `${lo}–${hi}${u}`;
    return lo != null ? `≥ ${lo}${u}` : `≤ ${hi}${u}`;
};

/** The checklist itself — numbered steps, one control per kind. */
export default function SopChecklistForm({ template, answers, onChange, disabled = false }) {
    if (!template) return null;
    const set = (id, v) => onChange({ ...answers, [id]: v });

    return (
        <ol className="list-unstyled mb-0 text-start" style={{ display: "grid", gap: 8 }}>
            {(template.items || []).map((it, i) => {
                const val = answers[it.itemid];
                const ev = evaluateItem(it, val);
                const touched = it.kind === "CHECK" ? false : val !== "" && val != null;
                const outOfRange = it.kind === "NUMBER" && touched && !ev.ok;
                const range = it.kind === "NUMBER" ? rangeText(it) : null;
                return (
                    <li key={it.itemid} className="d-flex align-items-start gap-2">
                        <span className="text-muted" style={{ minWidth: 22, fontVariantNumeric: "tabular-nums" }}>{i + 1}.</span>
                        <div className="flex-grow-1">
                            {it.kind === "CHECK" ? (
                                <label className="d-flex align-items-start gap-2 mb-0" style={{ cursor: "pointer" }}>
                                    <input
                                        type="checkbox"
                                        className="form-check-input mt-1"
                                        checked={val === true}
                                        disabled={disabled}
                                        onChange={(e) => set(it.itemid, e.target.checked)}
                                    />
                                    <span>{it.prompt}{!it.required && <span className="text-muted"> (optional)</span>}</span>
                                </label>
                            ) : (
                                <>
                                    <div className="mb-1">
                                        {it.prompt}
                                        {!it.required && <span className="text-muted"> (optional)</span>}
                                        {range && <span className="text-muted small ms-2">target {range}</span>}
                                    </div>
                                    <input
                                        type={it.kind === "NUMBER" ? "number" : "text"}
                                        inputMode={it.kind === "NUMBER" ? "decimal" : undefined}
                                        style={{ ...inputStyle, maxWidth: it.kind === "NUMBER" ? 180 : "100%",
                                            ...(outOfRange ? { borderColor: "var(--bs-danger)" } : {}) }}
                                        value={val ?? ""}
                                        disabled={disabled}
                                        onChange={(e) => set(it.itemid, e.target.value)}
                                    />
                                    {outOfRange && <div className="small text-danger mt-1">Outside target — fix before continuing</div>}
                                </>
                            )}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}
